import React from 'react';
import { isValid } from 'date-fns';
import {
  Box,
  Flex,
  Body,
  Image,
  SkeletonBox,
  Heading,
} from '@edreamsodigeo/prisma-design-system';
import {
  createFeature,
  useBrand,
  useLocale,
  useTranslation,
} from '@edreamsodigeo/frontend-core';

import { PRIME_DAYS_BANNER_FLIGHT_SUMMARY_MOBILE } from '@/versionedComponents';

export interface PrimeDaysBannerProps {
  startDate?: string;
  endDate?: string;
  withLogo?: boolean;
}

interface PrimeDaysWrapperProps {
  startDate?: string;
  endDate?: string;
}

const cleanKeys = (value: string): string =>
  value.replace('‘', '').replace('’', '');

export const PrimeDaysWrapper: React.FC<PrimeDaysWrapperProps> = ({
  startDate,
  endDate,
  children,
}) => {
  const currentDate = new Date();
  const { t } = useTranslation();
  const campaignStartDate =
    startDate && isValid(new Date(startDate))
      ? new Date(startDate)
      : new Date(cleanKeys(t('promobanner.startDate')));
  const campaignEndsDate =
    endDate && isValid(new Date(endDate))
      ? new Date(endDate)
      : new Date(cleanKeys(t('promobanner.endDate')));

  const isPrimeDays =
    currentDate >= campaignStartDate && currentDate <= campaignEndsDate;

  if (!isPrimeDays) {
    return null;
  }
  return <>{children}</>;
};

const PrimeDaysSkeletonComponent: React.FC = () => (
  <PrimeDaysWrapper>
    <SkeletonBox height="85px" />
  </PrimeDaysWrapper>
);

export const PrimeDaysSkeleton = createFeature({
  name: PRIME_DAYS_BANNER_FLIGHT_SUMMARY_MOBILE,
  variations: [null, PrimeDaysSkeletonComponent, PrimeDaysSkeletonComponent],
  version: 1,
});

const PrimeDaysBannerComponentWithoutLogo: React.FC<PrimeDaysBannerProps> = (
  props
) => {
  return <PrimeDaysBannerComponent {...props} withLogo={false} />;
};

const PrimeDaysBannerComponent: React.FC<PrimeDaysBannerProps> = ({
  startDate,
  endDate,
  withLogo = true,
}) => {
  const { t } = useTranslation();
  const brand = useBrand();
  const locale = useLocale();
  const logo = `/images/onefront/bluestone/${brand}/campaign_contentimage_pax_l_negative_${locale.split('-')[0]}.png`;
  const background = `url('/images/onefront/bluestone/${brand}/campaign_pax_${!withLogo ? 's' : 'l'}_negative.png') no-repeat 65% center`;
  const PillProperties = {
    ED: { background: 'secondary.50', color: 'primary.50' },
    GO: { background: 'primary.50', color: 'neutral.0' },
    OP: { background: 'primary.50', color: 'neutral.0' },
  };
  return (
    <PrimeDaysWrapper startDate={startDate} endDate={endDate}>
      <Box sx={{ background: background, backgroundSize: 'cover' }}>
        <Flex
          width="100%"
          py="base"
          pl="base"
          pr="2x_small"
          flexDirection="row"
          alignItems="center"
        >
          <Flex width={!withLogo ? '175px' : '152px'} flexDirection="column">
            {withLogo && (
              <>
                <Box pb="1x_small">
                  <Image
                    src={logo}
                    alt="No results image"
                    sx={{ width: '152px' }}
                  />
                </Box>
                <Box
                  backgroundColor={PillProperties[brand].background}
                  width="84px"
                >
                  <Body
                    color={PillProperties[brand].color}
                    type="xxSmall"
                    textAlign="center"
                    weight="bold"
                  >
                    {t('promobanner.dates')}
                  </Body>
                </Box>
              </>
            )}
            {!withLogo && (
              <Box>
                <Heading
                  color="neutral.0"
                  type="1"
                  weight="bold"
                  sx={{ lineHeight: '92%' }}
                >
                  {t('promobanner.pill')}
                </Heading>
              </Box>
            )}
          </Flex>
          <Box pt={!withLogo ? '0' : 'small'} pl="big" width="170px">
            <Body
              type={!withLogo ? 'base' : 'xSmall'}
              color="neutral.0"
              weight="normal"
              sx={{ lineHeight: !withLogo ? '100%' : '14px' }}
            >
              {t('promobanner.claim')}
            </Body>
          </Box>
        </Flex>
      </Box>
    </PrimeDaysWrapper>
  );
};

const PrimeDaysBanner = createFeature({
  name: PRIME_DAYS_BANNER_FLIGHT_SUMMARY_MOBILE,
  variations: [
    null,
    PrimeDaysBannerComponent,
    PrimeDaysBannerComponentWithoutLogo,
  ],
  version: 1,
});

export default PrimeDaysBanner;
